import { useEffect, useState } from 'react';
import { CATEGORIAS_DOCUMENTO } from '../../publicacoes/publicacao.definitions';
import type { CategoriaDocumento, Publicacao } from '../../publicacoes/publicacao.types';
import { FORM_VAZIO, formDe } from '../../publicacoes/editor/publicacaoEditor';
import type { FormPublicacao } from '../../publicacoes/editor/publicacaoEditor';
import { EditorCienciaDestaque } from '../../publicacoes/editor/EditorCienciaDestaque';
import { publicacaoService } from '../../publicacoes/publicacaoService';
import { ArquivoDocumentoCampo, ESCOLHA_VAZIA, descartarEnvio, escolhaDe } from './ArquivoDocumentoCampo';
import { ChipArquivo, EnvioArquivo } from '../../publicacoes/components/EnvioArquivo';
import { DrawerLateral } from './DrawerLateral';
import { useSalvarDocumento } from './useSalvarDocumento';
import type { AcaoDocumento, FormDocumento } from './useSalvarDocumento';

interface DocumentoEditorDrawerProps {
  isOpen: boolean;
  documento: Publicacao | null;
  onClose: () => void;
}

const ROTULO = 'block text-sm font-medium text-raro-gray-700';

function formDocumentoDe(documento: Publicacao | null): FormDocumento {
  return {
    base: documento ? formDe(documento) : FORM_VAZIO,
    categoria: (documento?.categoria as CategoriaDocumento) ?? '',
    escolha: documento ? escolhaDe(documento.arquivo) : ESCOLHA_VAZIA,
    anunciarNoInicio: documento?.anunciarNoInicio ?? true,
  };
}

/** Novo documento / editar dados (trocar o arquivo sem virar versão nova). Versão nova é outro drawer. */
export function DocumentoEditorDrawer({ isOpen, documento, onClose }: DocumentoEditorDrawerProps) {
  const [form, setForm] = useState<FormDocumento>(() => formDocumentoDe(null));
  const [idReservado, setIdReservado] = useState(() => publicacaoService.novoId());
  const { salvar, salvando, erros, limparErros } = useSalvarDocumento();
  const status = documento?.status ?? 'rascunho';
  const jaPublicado = status === 'publicada' || status === 'despublicada';

  useEffect(() => {
    if (isOpen) {
      setForm(formDocumentoDe(documento));
      setIdReservado(documento?.id ?? publicacaoService.novoId());
      limparErros();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isOpen, documento?.id]);

  const alterarBase = (parcial: Partial<FormPublicacao>) => setForm((atual) => ({ ...atual, base: { ...atual.base, ...parcial } }));
  const [recemEnviados, setRecemEnviados] = useState<string[]>([]);
  const removerAnexo = (path: string) =>
    setForm((atual) => {
      const b = atual.base;
      const enviadoAgora = b.pathsEnviados.includes(path);
      if (enviadoAgora) void publicacaoService.removerArquivo(path);
      return {
        ...atual,
        base: {
          ...b,
          anexos: b.anexos.filter((a) => a.path !== path),
          pathsEnviados: b.pathsEnviados.filter((p) => p !== path),
          pathsRemovidos: enviadoAgora ? b.pathsRemovidos : [...b.pathsRemovidos, path],
        },
      };
    });
  const executar = async (acao: AcaoDocumento) => {
    if (await salvar(form, documento, acao, idReservado)) onClose();
  };

  return (
    <DrawerLateral
      isOpen={isOpen}
      titulo={documento ? 'Editar documento' : 'Novo documento'}
      onClose={() => {
        if (salvando) return;
        descartarEnvio(form.escolha);
        form.base.pathsEnviados.forEach((path) => void publicacaoService.removerArquivo(path));
        onClose();
      }}
      erros={erros}
      rodape={
        jaPublicado ? (
          <button type="button" className="btn-primary" disabled={salvando} onClick={() => executar('salvar')}>
            {salvando ? 'Salvando...' : 'Salvar alterações'}
          </button>
        ) : (
          <>
            <button type="button" className="btn-secondary" disabled={salvando} onClick={() => executar('rascunho')}>
              Salvar rascunho
            </button>
            <button type="button" className="btn-primary" disabled={salvando} onClick={() => executar('publicar')}>
              {salvando ? 'Salvando...' : form.base.agendar ? 'Agendar' : 'Publicar agora'}
            </button>
          </>
        )
      }
    >
      <label className="block">
        <span className={ROTULO}>Título *</span>
        <input
          className="input-primary mt-1 w-full"
          maxLength={140}
          value={form.base.titulo}
          onChange={(event) => alterarBase({ titulo: event.target.value })}
        />
      </label>
      <label className="block">
        <span className={ROTULO}>Categoria *</span>
        <select
          className="input-primary mt-1 w-full"
          value={form.categoria}
          onChange={(event) => setForm((atual) => ({ ...atual, categoria: event.target.value as CategoriaDocumento | '' }))}
        >
          <option value="">Selecione</option>
          {CATEGORIAS_DOCUMENTO.map((c) => (
            <option key={c.valor} value={c.valor}>
              {c.label}
            </option>
          ))}
        </select>
      </label>
      <label className="block">
        <span className={ROTULO}>Descrição *</span>
        <input
          className="input-primary mt-1 w-full"
          maxLength={240}
          placeholder="Do que se trata, em uma frase"
          value={form.base.resumo}
          onChange={(event) => alterarBase({ resumo: event.target.value })}
        />
      </label>
      <ArquivoDocumentoCampo
        publicacaoId={idReservado}
        valor={form.escolha}
        atual={documento?.arquivo}
        onChange={(escolha) => setForm((atual) => ({ ...atual, escolha }))}
      />
      {jaPublicado && (
        <p className="rounded-lg bg-raro-gray-50 px-3 py-2 text-xs text-raro-gray-600">
          Trocar o arquivo aqui corrige a versão atual. Para uma versão nova (com histórico e, se quiser, nova ciência), use
          &quot;Nova versão&quot; na lista.
        </p>
      )}
      <div className="space-y-2">
        <span className={ROTULO}>Anexos complementares (opcional)</span>
        {form.base.anexos.map((anexo) => (
          <ChipArquivo
            key={anexo.path}
            anexo={anexo}
            recemEnviado={recemEnviados.includes(anexo.path)}
            onRemover={() => removerAnexo(anexo.path)}
          />
        ))}
        <EnvioArquivo
          publicacaoId={idReservado}
          pasta="anexos"
          multiplo
          accept="application/pdf,image/png,image/jpeg,image/webp"
          texto="Adicionar formulário, apêndice... (PDF ou imagem, até 15 MB cada)"
          onEnviado={(anexo) => {
            setRecemEnviados((atual) => [...atual, anexo.path]);
            setForm((atual) => ({
              ...atual,
              base: { ...atual.base, anexos: [...atual.base.anexos, anexo], pathsEnviados: [...atual.base.pathsEnviados, anexo.path] },
            }));
          }}
        />
      </div>
      <label className="flex items-center gap-2 text-sm">
        <input
          type="checkbox"
          className="h-4 w-4 rounded text-primary-600"
          checked={form.anunciarNoInicio}
          onChange={(event) => setForm((atual) => ({ ...atual, anunciarNoInicio: event.target.checked }))}
        />
        Anunciar na Início (aparece no feed de informativos)
      </label>
      <EditorCienciaDestaque form={form.base} onChange={alterarBase} status={status} mostrarDestaque={false} />
    </DrawerLateral>
  );
}
